import { oneApi } from 'config/api'

export default {
  data() {
    return {
      notificationList: [],
      pollingTimer: null,
      isLoading: false,
      shouldIgnoreFetchResult: false, // Flag to ignore fetchReminders result
      // Mock data
      // mockData: [
      //   {
      //     planId: 504554,
      //     courseId: 94401,
      //     packageId: 100001,
      //     subPlatformType: 2, // 0: big class，1: small class，2: real small class
      //     lessonType: 'FORMAL', // 'FORMAL' | 'PLAYBACK' | 'AUDITION' | 'TEMPORARY'
      //     serverTime: new Date().getTime(),
      //     startTime: new Date().getTime() + 10 * 60 * 1000,
      //     lessonNum: 1,
      //     lessonName: 'Magic Math',
      //     bizId: 1,
      //     classId: 1,
      //   },
      //   {
      //     planId: 504555,
      //     courseId: 94402,
      //     packageId: 100002,
      //     subPlatformType: 1, // 0: big class，1: small class，2: real small class
      //     lessonType: 'FORMAL', // 'FORMAL' | 'PLAYBACK' | 'AUDITION' | 'TEMPORARY'
      //     serverTime: new Date().getTime(),
      //     startTime: new Date().getTime() - 10 * 60 * 1000,
      //     lessonNum: 2,
      //     lessonName: 'Magic Math',
      //     bizId: 1,
      //     classId: 2,
      //   },
      // ],
    }
  },
  computed: {
    isLogin() {
      return this.$store.state.ucenter.isLogin
    },
    currentUserId() {
      return this.$store.state.ucenter.user?.uid || ''
    },
  },
  watch: {
    isLogin(newVal) {
      if (newVal) {
        this.startPolling()
      } else {
        this.stopPolling()
        this.notificationList = []
        this.shouldIgnoreFetchResult = false // Reset flag
      }
    },
    // watch current user id change
    currentUserId(newUid, oldUid) {
      // if user id changed (from empty to value, or from one value to another value, or logout)
      if (newUid !== oldUid) {
        // 1. set flag to ignore fetch result
        this.shouldIgnoreFetchResult = true

        // 2. clear current data
        this.notificationList = []

        // 3. clear polling timer
        this.stopPolling()

        // 4. reset flag to allow fetch result
        this.shouldIgnoreFetchResult = false

        // 5. if logged in, start polling
        if (this.isLogin) {
          this.startPolling()
        }
      }
    },
  },
  mounted() {
    // mock data
    // this.initMockData()
    if (this.isLogin) {
      this.startPolling()
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    /**
     * init notification list
     */
    initMockData() {
      this.notificationList = this.mockData.map((item, index) => this.transformNotificationData(item, index))
    },
    /**
     * transform notification data to display format
     * @param {Object} data original data
     * @param {Number} index index
     * @returns {Object} transformed notification data
     */
    transformNotificationData(data, index) {
      // generate title and content based on type
      let title; let
          content
      const type = data.serverTime < data.startTime ? 'start_soon' : 'in_progress'
      const className = `${data.lessonName} Lesson ${data.lessonNum}`
      if (type === 'start_soon') { // start soon
        title = 'Your Class is About to Start!'
        content = `<p>Get ready! Your upcoming class "${className}" is starting soon.</p> <p>Click below to join and not miss a moment. </p> <p style="font-style: italic;">If you're already in class, please ignore this message.</p>`
      } else if (type === 'in_progress') { // in progress
        title = 'Hurry! Your Class is Already in Progress!'
        content = `<p>It's time to start your magical journey with your "${className}" teacher.</p> <p>Click below to join and not miss a moment. </p> <p style="font-style: italic;">If you're already in class, please ignore this message.</p>`
      }

      return {
        id: `notification_${data.planId}_${index}`,
        planId: data.planId,
        courseId: data.courseId,
        subPlatformType: data.subPlatformType,
        bizId: data.bizId,
        lessonType: data.lessonType,
        type, // 'start_soon' | 'in_progress'
        title,
        content, // dom string
        className, // string
        buttonText: 'Join Now',
        classId: data.classId,
        enterStartTime: data.enterStartTime,
      }
    },
    async fetchReminders() {
      if (!this.isLogin || this.isLoading) return

      this.isLoading = true
      const shouldIgnore = this.shouldIgnoreFetchResult // Save flag before async operation

      try {
        const res = await this.$axios.post(
          oneApi.getWebReminder,
          {},
          { rewritePostBody: true },
        )

        // Ignore result if flag is set
        if (shouldIgnore) {
          return
        }

        if (Number(res.code) === 0 && res.data) {
          // notify list
          const rawList = res.data || []
          this.notificationList = rawList.map((item, index) => this.transformNotificationData(item, index)).filter((item) => item && item.title)
        } else {
          this.notificationList = []
        }
      } catch (error) {
        // Ignore result if flag is set
        if (shouldIgnore) {
          return
        }
        this.notificationList = []
      } finally {
        this.isLoading = false
        // schedule next poll only if not ignoring results
        if (!shouldIgnore) {
          this.scheduleNextPoll()
        }
      }
    },

    /**
     * retry with delay
     * @param {Function} fn - function to retry
     * @param {Number} maxRetries - maximum retries, default 3 times
     * @param {Number} delay - retry delay time (milliseconds), default 3000ms
     * @returns {Promise} return Promise, fail silently
     */
    async retryWithDelay(fn, maxRetries = 3, delay = 3000) {
      // Retry logic requires sequential execution, so await-in-loop is necessary
      // eslint-disable-next-line no-plusplus, no-await-in-loop
      for (let attempt = 0; attempt < maxRetries; attempt += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await fn()
          return // success return
        } catch (error) {
          // if last attempt, fail silently
          if (attempt === maxRetries - 1) {
            return
          }
          // await retry delay
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, delay))
        }
      }
    },

    async closeNotification(notification) {
      // use retry mechanism: fail and retry 3 times, every 3 seconds, finally fail silently
      await this.retryWithDelay(
        async () => {
          await this.$axios.post(
            oneApi.closeWebReminder,
            { lessonType: notification.lessonType, enterStartTime: notification.enterStartTime, classId: notification.classId },
            { rewritePostBody: true },
          )
        },
        3, // maximum retries 3 times
        3000, // fail and retry 3 seconds
      )
    },
    scheduleNextPoll() {
      // clear previous timer
      if (this.pollingTimer) {
        clearTimeout(this.pollingTimer)
      }
      // schedule next poll
      this.pollingTimer = setTimeout(() => {
        if (this.isLogin) {
          this.fetchReminders()
        }
      }, 60 * 1000) // one minute
    },
    startPolling() {
      // immediately call once
      this.fetchReminders()
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearTimeout(this.pollingTimer)
        this.pollingTimer = null
      }
      // Don't clear notificationList here, as we want to keep the filtered list
    },
    formatContent(content, className) {
      if (!content) return ''
      // highlight class name
      if (className) {
        // 直接字符串替换（区分大小写）
        return content.replace(
          className,
          `<strong style="color: #FF5A00;">${className}</strong>`,
        )
      }
      return content
    },
    async handleClose(notification) {
      // 1. Immediately filter out the notification from data
      if (notification && notification.id) {
        this.notificationList = this.notificationList.filter((item) => item.id !== notification.id)
      }

      // 2. Stop polling - clear timer
      this.stopPolling()

      // 3. Set flag to ignore results from ongoing fetchReminders request
      this.shouldIgnoreFetchResult = true

      // 4. Wait for closeNotification to complete (success or retry 3 times failed)
      await this.closeNotification(notification)

      // 5. Clear flag and restart polling after closeNotification completes
      this.shouldIgnoreFetchResult = false
      if (this.isLogin) {
        this.startPolling()
      }
    },
    handleJoin(notification) {
      // handle join class logic
      this.closeNotification(notification)
      const {
        planId, courseId, classId, subPlatformType, bizId, lessonType,
      } = notification || {}
      if (notification.joinUrl) {
        window.open(notification.joinUrl, '_blank')
      } else if (planId && courseId) {
        // build join url based on planId, courseId, subPlatformType, bizId
        const joinUrl = `/study#/classroom?planId=${planId}&classId=${classId}&subPlatformType=${subPlatformType}&bizId=${bizId}&lessonType=${lessonType}`
        window.open(joinUrl, '_blank')
      }
    },
  },
}
